import { type FormEvent, useState } from 'react';
import { ServerError, signIn } from '../account';
import { type MessageKey, useT } from '../i18n';
import { LEGAL } from '../legal';
import { importLocalSaves } from '../saves';
import { savePrefs, useGame } from '../store';
import { Icon } from './icons';

/** Server error codes with their own message (anything else: a general failure). */
const ERRORS = new Set([
  'badUsername',
  'rudeUsername',
  'badEmail',
  'emailTaken',
  'badPassword',
  'taken',
  'wrongLogin',
  'slowDown',
  'offline',
  'mismatch',
]);

/**
 * Sign-in and sign-up (the title screen's account page): with an account the saved games are
 * kept on the server, the same ones on every device; this device's move to it on signing in.
 */
export function AuthPanel({ onSignedIn }: { onSignedIn: (moved: number) => void }) {
  const t = useT();
  const [kind, setKind] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (kind === 'register' && password !== repeat) {
      setError('mismatch');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const account = await signIn(kind, { login: username.trim(), email: email.trim(), password });
      const state = useGame.getState();
      // The account name is the default name shown to co-op partners.
      if (!state.playerName.trim()) {
        useGame.setState({ playerName: account.username });
        savePrefs({ ...useGame.getState() });
      }
      // This device's games (played without an account) move to the account.
      const moved = await importLocalSaves().catch(() => 0);
      onSignedIn(moved);
    } catch (err) {
      setError(err instanceof ServerError ? err.code : 'server');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="auth-panel" onSubmit={(e) => void submit(e)}>
      <div className="segmented auth-tabs">
        {(['login', 'register'] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={kind === k ? 'active' : ''}
            aria-pressed={kind === k}
            onClick={() => {
              setKind(k);
              setError(null);
            }}
          >
            {t(`auth.${k}` as MessageKey)}
          </button>
        ))}
      </div>
      <p className="muted small">{t(kind === 'login' ? 'auth.loginLead' : 'auth.registerLead')}</p>
      <label className="field">
        <span>{t(kind === 'login' ? 'auth.loginName' : 'auth.username')}</span>
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          maxLength={kind === 'login' ? 254 : 20}
          spellCheck={false}
          required
        />
      </label>
      {kind === 'register' && (
        <label className="field">
          <span>{t('auth.email')}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            maxLength={254}
            spellCheck={false}
            required
          />
        </label>
      )}
      <label className="field">
        <span>{t('auth.password')}</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={kind === 'login' ? 'current-password' : 'new-password'}
          maxLength={128}
          required
        />
      </label>
      {kind === 'register' && (
        <label className="field">
          <span>{t('auth.repeat')}</span>
          <input
            type="password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            autoComplete="new-password"
            maxLength={128}
            required
          />
        </label>
      )}
      {kind === 'register' && <p className="muted small">{t('auth.rules')}</p>}
      {kind === 'register' && (
        <div className="auth-agree">
          <label className="check-row">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              required
            />
            <span>{t('auth.agree', { age: LEGAL.accountAge })}</span>
          </label>
          <span className="auth-agree-links">
            <button
              type="button"
              className="link-btn"
              onClick={() => useGame.setState({ legal: 'terms' })}
            >
              {t('legal.terms')}
            </button>
            <button
              type="button"
              className="link-btn"
              onClick={() => useGame.setState({ legal: 'privacy' })}
            >
              {t('legal.privacy')}
            </button>
          </span>
        </div>
      )}
      {error && (
        <div className="banner error small" role="alert">
          <Icon name="warning" size={16} />
          {t(`auth.error.${ERRORS.has(error) ? error : 'server'}` as MessageKey)}
        </div>
      )}
      <button
        type="submit"
        className="btn primary xl"
        disabled={busy || (kind === 'register' && !agreed)}
      >
        <Icon name="play" size={18} />
        {t(busy ? 'auth.wait' : kind === 'login' ? 'auth.login' : 'auth.register')}
      </button>
    </form>
  );
}
